import { useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { buildSportsKeywords } from "@/../../shared/seoKeywords";

const ORIGIN = "https://korasport-eaxuwpty.manus.space";
const SHARE_IMAGE = `${ORIGIN}/manus-storage/kora-arabia-search-thumbnail_bc1078c6.png`;
const BASE_KEYWORDS = buildSportsKeywords(["كرة القدم", "كورة أرابيا"]);

type SeoMeta = { title: string; description: string; keywords: string[] };
type ArticleSeoData = { title: string; excerpt: string; content?: string | null; category?: string | null; club?: string | null; publishedAt?: Date | string | null };
type MatchSeoData = { homeTeam: string; awayTeam: string; competition: string; status?: string | null };

const DEFAULT: SeoMeta = {
  title: "كورة أرابيا | أخبار ونتائج كرة القدم",
  description: "منصة عربية لمتابعة أخبار كرة القدم المصرية والعالمية، المباريات، النتائج، ترتيب الدوريات وملخصات الأهداف.",
  keywords: BASE_KEYWORDS,
};
const META: Record<string, SeoMeta> = {
  "/": DEFAULT,
  "/news": { title: "أخبار كرة القدم العربية والعالمية | كورة أرابيا", description: "اقرأ أحدث أخبار كرة القدم المصرية والعربية والعالمية في مقالات عربية واضحة من مصادر موثوقة.", keywords: ["أخبار كرة القدم", "أخبار الكرة المصرية", "أخبار كرة القدم العالمية", "أخبار الأهلي", "أخبار الزمالك", ...BASE_KEYWORDS] },
  "/matches": { title: "جدول مباريات اليوم والنتائج المباشرة | كورة أرابيا", description: "تابع جدول مباريات اليوم والنتائج وحالات المباريات بتوقيت القاهرة.", keywords: ["مباريات اليوم", "جدول المباريات", "النتائج المباشرة", "مواعيد مباريات كرة القدم", ...BASE_KEYWORDS] },
  "/matches/archive": { title: "أرشيف نتائج ومباريات كرة القدم اليومية | كورة أرابيا", description: "استعرض أرشيف مباريات كرة القدم ونتائجها حسب التاريخ بتوقيت القاهرة.", keywords: ["أرشيف المباريات", "نتائج مباريات كرة القدم", "مباريات الأمس", "نتائج اليوم", ...BASE_KEYWORDS] },
  "/archive": { title: "الأرشيف اليومي للمباريات والمقالات | كورة أرابيا", description: "تصفح جميع مباريات ومقالات كرة القدم اليومية حسب التاريخ مع روابط داخلية مباشرة.", keywords: ["الأرشيف اليومي", "أرشيف مباريات اليوم", "أرشيف أخبار كرة القدم", "مقالات مباريات اليوم", ...BASE_KEYWORDS] },
  "/standings": { title: "ترتيب الفرق والدوريات | كورة أرابيا", description: "تعرّف على ترتيب الفرق والنقاط والأهداف في أبرز الدوريات المحلية والعالمية.", keywords: ["ترتيب الفرق", "ترتيب الدوريات", "جدول الدوري", "النقاط والأهداف", ...BASE_KEYWORDS] },
  "/highlights": { title: "ملخصات أهداف المباريات | كورة أرابيا", description: "شاهد ملخصات الأهداف الرسمية المتاحة للمباريات المحلية والعالمية.", keywords: ["ملخصات الأهداف", "فيديوهات مباريات", "أهداف اليوم", "ملخص مباريات كرة القدم", ...BASE_KEYWORDS] },
  "/clubs/ahly": { title: "أخبار النادي الأهلي | كورة أرابيا", description: "تابع أخبار النادي الأهلي ونتائجه وأبرز تغطيته الرياضية.", keywords: ["الأهلي", "أخبار الأهلي", "مباريات الأهلي", "نتائج الأهلي", ...BASE_KEYWORDS] },
  "/clubs/zamalek": { title: "أخبار نادي الزمالك | كورة أرابيا", description: "تابع أخبار نادي الزمالك ونتائجه وأبرز تغطيته الرياضية.", keywords: ["الزمالك", "أخبار الزمالك", "مباريات الزمالك", "نتائج الزمالك", ...BASE_KEYWORDS] },
  "/privacy": { title: "سياسة الخصوصية | كورة أرابيا", description: "تعرف على كيفية تعامل كورة أرابيا مع بيانات الزوار والخصوصية.", keywords: ["سياسة الخصوصية", "خصوصية الزوار", "كورة أرابيا"] },
  "/contact": { title: "اتصل بنا | كورة أرابيا", description: "تواصل مع إدارة كورة أرابيا للاستفسارات والاقتراحات والإبلاغ عن الأخطاء.", keywords: ["اتصل بنا", "تواصل مع كورة أرابيا", "إدارة الموقع"] },
  "/terms": { title: "شروط الاستخدام | كورة أرابيا", description: "الشروط المنظمة لاستخدام موقع كورة أرابيا ومحتواه وروابطه الخارجية.", keywords: ["شروط الاستخدام", "قوانين الموقع", "كورة أرابيا"] },
};

function uniqueKeywords(values: string[]) {
  return Array.from(new Set(values.flatMap((value) => value.split(/[،,|•؛\n]+/).map((item) => item.trim()).filter((item) => item.length >= 3)).filter(Boolean))).slice(0, 20);
}

export function buildSeoKeywords(parts: string[]) {
  return uniqueKeywords(buildSportsKeywords([...parts, ...BASE_KEYWORDS]));
}

function truncate(value: string, max: number) {
  const clean = value.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trim()}…` : clean;
}

export function buildArticleSeo(article: ArticleSeoData): SeoMeta {
  const title = `${truncate(article.title, 58)} | كورة أرابيا`;
  const description = truncate(article.excerpt || article.content || `أحدث تفاصيل خبر ${article.title} من كورة أرابيا.`, 155);
  return { title, description, keywords: buildSeoKeywords([article.title, article.category || "", article.club || "", article.club ? `أخبار ${article.club}` : "", article.club ? `مباريات ${article.club}` : "", "موعد المباراة", "نتيجة المباراة", "خبر رياضي", "مقال كرة قدم"]) };
}

function getEgyptToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Cairo" }).format(new Date());
}

export function buildMatchesSeo(matches: MatchSeoData[], date: string): SeoMeta {
  const teams = matches.slice(0, 8).flatMap((match) => [match.homeTeam, match.awayTeam]);
  const competitions = Array.from(new Set(matches.slice(0, 8).map((match) => match.competition)));
  const suffix = date === getEgyptToday() ? "اليوم" : `بتاريخ ${date}`;
  const description = matches.length > 0
    ? truncate(`تابع ${matches.length} مباراة ${suffix} مع مواعيد ونتائج كرة القدم من ${competitions.slice(0, 2).join(" و")}.`, 155)
    : `جدول مباريات كرة القدم ${suffix} والنتائج المحدثة بتوقيت القاهرة من كورة أرابيا.`;
  return { title: `مباريات كرة القدم ${suffix} والنتائج | كورة أرابيا`, description, keywords: buildSeoKeywords(["مباريات كرة القدم", "جدول المباريات", ...teams, ...competitions]) };
}

function getArticleId(path: string) {
  const match = path.match(/^\/article\/(\d+)$/);
  return match ? Number(match[1]) : null;
}

function getArchiveDate(path: string) {
  const match = path.match(/^(?:\/matches\/archive|\/archive)\/(\d{4}-\d{2}-\d{2})$/);
  return match?.[1] ?? null;
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
  if (!element) { element = document.createElement("meta"); element.setAttribute(attribute, key); document.head.appendChild(element); }
  element.content = content;
}

export function getStructuredData(path: string, article?: ArticleSeoData | null, matches: MatchSeoData[] = []) {
  const archiveDate = getArchiveDate(path);
  const breadcrumbs = article
    ? [
        { name: "الرئيسية", url: `${ORIGIN}/` },
        { name: "أخبار كرة القدم", url: `${ORIGIN}/news` },
        { name: truncate(article.title, 70), url: `${ORIGIN}${path}` },
      ]
    : archiveDate
      ? [
          { name: "الرئيسية", url: `${ORIGIN}/` },
          { name: "المباريات", url: `${ORIGIN}/matches` },
          { name: path.startsWith("/matches/") ? "أرشيف المباريات" : "الأرشيف اليومي", url: `${ORIGIN}${path.startsWith("/matches/") ? "/matches/archive" : "/archive"}` },
          { name: archiveDate, url: `${ORIGIN}${path}` },
        ]
      : null;
  const sectionNames: Record<string, string> = { "/": "كورة أرابيا", "/news": "أخبار كرة القدم", "/matches": "جدول مباريات اليوم", "/matches/archive": "أرشيف نتائج مباريات كرة القدم", "/archive": "الأرشيف اليومي للمباريات والمقالات", "/standings": "ترتيب الفرق والدوريات", "/highlights": "ملخصات أهداف المباريات" };
  const graph: Record<string, unknown>[] = [
    { "@type": "WebSite", "@id": `${ORIGIN}/#website`, name: "كورة أرابيا", url: `${ORIGIN}/`, inLanguage: "ar", description: DEFAULT.description },
    { "@type": "SportsOrganization", "@id": `${ORIGIN}/#organization`, name: "كورة أرابيا", url: `${ORIGIN}/`, description: DEFAULT.description },
  ];
  if (article) graph.push({ "@type": "NewsArticle", "@id": `${ORIGIN}${path}#article`, headline: article.title, description: article.excerpt, datePublished: article.publishedAt ? new Date(article.publishedAt).toISOString() : undefined, inLanguage: "ar", mainEntityOfPage: `${ORIGIN}${path}`, publisher: { "@id": `${ORIGIN}/#organization` } });
  if (breadcrumbs) graph.push({ "@type": "BreadcrumbList", "@id": `${ORIGIN}${path}#breadcrumb`, itemListElement: breadcrumbs.map((item, index) => ({ "@type": "ListItem", position: index + 1, name: item.name, item: item.url })) });
  const name = archiveDate ? `أرشيف مباريات ${archiveDate}` : sectionNames[path];
  if (name) graph.push({ "@type": "CollectionPage", "@id": `${ORIGIN}${path}#page`, name, url: `${ORIGIN}${path === "/" ? "/" : path}`, isPartOf: { "@id": `${ORIGIN}/#website` }, inLanguage: "ar" });
  if (matches.length > 0 && (path === "/matches" || archiveDate)) graph.push(...matches.slice(0, 20).map((match) => ({ "@type": "SportsEvent", name: `${match.homeTeam} ضد ${match.awayTeam}`, sport: "Football", homeTeam: { "@type": "SportsTeam", name: match.homeTeam }, awayTeam: { "@type": "SportsTeam", name: match.awayTeam }, eventStatus: match.status === "finished" ? "https://schema.org/EventCompleted" : match.status === "live" ? "https://schema.org/EventInProgress" : "https://schema.org/EventScheduled" })));
  return { "@context": "https://schema.org", "@graph": graph };
}

export function SeoHead() {
  const [location] = useLocation();
  const path = location || "/";
  const articleId = getArticleId(path);
  const archiveDate = getArchiveDate(path);
  const [todayDate] = useMemo(() => [getEgyptToday()], []);
  const matchDate = path === "/matches" ? todayDate : archiveDate;
  const articleQuery = trpc.sports.articleById.useQuery({ id: articleId ?? 0 }, { enabled: articleId !== null, retry: false });
  const matchesQuery = trpc.sports.matchesByDate.useQuery({ date: matchDate ?? todayDate }, { enabled: Boolean(matchDate), retry: false });
  const article = articleQuery.data;
  const matches = matchesQuery.data ?? [];

  useEffect(() => {
    const baseMeta = META[path] ?? (archiveDate ? { title: `أرشيف مباريات ${archiveDate} | كورة أرابيا`, description: `نتائج ومباريات كرة القدم المؤرشفة بتاريخ ${archiveDate} بتوقيت القاهرة.`, keywords: buildSeoKeywords(["أرشيف المباريات", archiveDate]) } : DEFAULT);
    const meta = article ? buildArticleSeo(article) : matchDate ? buildMatchesSeo(matches, matchDate) : baseMeta;
    const canonical = `${ORIGIN}${path === "/" ? "/" : path}`;
    document.title = meta.title;
    setMeta("name", "description", meta.description);
    setMeta("name", "keywords", meta.keywords.join(", "));
    setMeta("name", "robots", "index,follow,max-image-preview:large");
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", article ? "article" : "website");
    setMeta("property", "og:site_name", "كورة أرابيا");
    setMeta("property", "og:locale", "ar_AR");
    setMeta("property", "og:image", SHARE_IMAGE);
    setMeta("property", "og:image:alt", "كورة أرابيا - أخبار ونتائج كرة القدم");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", SHARE_IMAGE);
    setMeta("name", "twitter:image:alt", "كورة أرابيا - أخبار ونتائج كرة القدم");
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
    let structured = document.head.querySelector('script[data-seo-jsonld="true"]') as HTMLScriptElement | null;
    if (!structured) { structured = document.createElement("script"); structured.type = "application/ld+json"; structured.dataset.seoJsonld = "true"; document.head.appendChild(structured); }
    structured.textContent = JSON.stringify(getStructuredData(path, article, matches));
  }, [path, archiveDate, article, matches, matchDate]);
  return null;
}
